import React, { createContext, useContext, useState, useEffect } from "react";

export const content = {
  en: {
    nav: {
      programs: "Programs",
      classifications: "Classifications",
      pricing: "Pricing",
      reviews: "Reviews",
      gallery: "Gallery",
      instructor: "Instructors",
      getQuote: "Get Quote",
      getInstantQuote: "Get Instant Quote",
    },
    hero: {
      reviews: "222 Google Reviews",
      tagline: "California's #1 Safety Certifications",
      lines: ["CERTIFIED", "TO OPERATE.", "BUILT TO"],
      last: "LAST.",
      sub: "OSHA-compliant forklift & aerial lift operator certification — delivered onsite at your facility in about 2.5 hours. Taught by a GSSC Certified Instructor.",
      cta: "Get Instant Quote",
      call: "Call",
      badges: ["English & Spanish", "All 7 Forklift Classes", "Photo ID Cards"],
    },
    marquee: [
      "California OSHA Compliant",
      "222 Five-Star Reviews",
      "Onsite Training At Your Facility",
      "Photo Authorization ID Cards",
      "English & Spanish Instruction",
      "GSSC Certified Instructors",
      "All 7 Forklift Classifications",
    ],
    manifesto: {
      label: "Why Certify With GSSC",
      title1: "Safety is a",
      title2: "competitive edge.",
      intro:
        "Four reasons California employers trust Golden State Safety Certifications to keep their operators compliant, confident and productive.",
      items: [
        { no: "01", title: "Zero OSHA Fines", body: "Operator certification isn't optional — it's federal law. We keep your crew compliant and your business clear of penalties that can reach six figures per violation." },
        { no: "02", title: "2.5-Hour Onsite Mastery", body: "We come to your facility and certify your team in about two and a half hours. No travel, no downtime, no production loss — training built around your schedule." },
        { no: "03", title: "All 7 Classifications", body: "From electric rider trucks to rough-terrain forklifts and aerial lifts, we cover every OSHA class. One instructor, every piece of equipment you run." },
        { no: "04", title: "Train-the-Trainer Ready", body: "Build your own in-house compliant program. Our certification kit equips your designated trainer to certify up to 20 operators — English and Spanish friendly." },
      ],
    },
    services: {
      label: "Certification Programs",
      title1: "Every operator.",
      title2: "Every machine.",
      intro:
        "Customized programs conducted at your site — in English or Spanish — to meet all Cal/OSHA requirements for general and construction industry.",
      requestCta: "Request this program",
      includesTitle: "Every Safety Program Includes",
      includes: [
        "Instructional safety driving skills",
        "Proper handling procedures",
        "Combo training — forklift & aerial",
        "Refueling & battery charging",
        "Daily equipment inspection",
        "Narrow aisle equipment",
      ],
      items: [
        { tag: "Program 01", title: "Forklift Operator Certification", desc: "OSHA-compliant certification on every type of powered industrial truck — offered onsite in English or Spanish.", points: ["Warehouse & Counterbalance", "Reach Truck & Order Picker", "Telehandler & Rough Terrain", "Pallet Jack & Walkie Stacker"] },
        { tag: "Program 02", title: "Aerial & Scissor Lift Certification", desc: "Convenient, cost-effective aerial lift safety programs meeting the highest Cal/OSHA compliance standards.", points: ["Scissor Lift Operation", "Boom & Articulating Lifts", "Fall Protection Protocols", "Jobsite Hazard Assessment"] },
        { tag: "Program 03", title: "Train-the-Trainer Certification", desc: "Develop a regulatory-compliant, in-house operator training program led by your own certified trainer.", points: ["Program Kit — 20 Certifications", "Spanish-Friendly Materials", "Certification Program $400", "Instructional Kit $600"] },
      ],
    },
    classes: {
      label: "OSHA Forklift Classes",
      title1: "We certify all",
      title2: " 7 classifications.",
      note: "Certification includes classroom, hands-on evaluation & photo ID card.",
      items: [
        { cls: "Class I", name: "Electric Motor Rider Trucks", use: "Counterbalanced sit-down and stand-up riders used in loading docks and general warehousing." },
        { cls: "Class II", name: "Electric Motor Narrow Aisle Trucks", use: "Reach trucks and order pickers designed for high-density, narrow-aisle storage." },
        { cls: "Class III", name: "Electric Motor Hand Trucks", use: "Walkie stackers and electric pallet jacks for ground-level material movement." },
        { cls: "Class IV", name: "Internal Combustion — Cushion Tire", use: "Sit-down cushion-tire trucks for smooth indoor surfaces and heavy loads." },
        { cls: "Class V", name: "Internal Combustion — Pneumatic Tire", use: "Rugged pneumatic-tire trucks for outdoor yards and mixed terrain." },
        { cls: "Class VI", name: "Electric & IC Engine Tractors", use: "Tow tractors used for pulling rather than lifting across large facilities." },
        { cls: "Class VII", name: "Rough Terrain Forklift Trucks", use: "Heavy-duty telehandlers built for construction sites and uneven ground." },
      ],
    },
    pricing: {
      label: "Transparent Pricing",
      title1: "The more you train,",
      title2: "the less you pay.",
      intro:
        "Per-person operator certification pricing. Additional class codes are just $65 each. Volume discounts applied automatically.",
      popular: "Popular",
      perPerson: "/person",
      addCode: "+ $65 per additional class code",
      tierNotes: ["Small crews & individuals", "Growing teams", "Best value per operator"],
      tierNames: ["1–5 Trainees", "6–10 Trainees", "11+ Trainees"],
      calcTitle: "Estimate your cost",
      operators: "Number of operators",
      codes: "Equipment class codes",
      rate: "Rate",
      perPersonShort: "/person",
      estTotal: "Estimated total",
      lockIn: "Lock in this quote",
      riskLabel: "The cost of non-compliance",
      riskBody:
        "Maximum federal OSHA penalty per willful or repeated violation for uncertified operators. Certification pays for itself many times over.",
      yourCost: "your compliance cost",
      trainer1Title: "Train-the-Trainer Certification",
      trainer1Body: "Certify your designated in-house trainer. Spanish-friendly instructional material available.",
      trainer2Title: "Train-the-Trainer Instructional Kit",
      trainer2Body: "Complete kit including materials for 20 certifications — build your own compliant program.",
    },
    reviews: {
      label: "Verified Reviews",
      title1: "Rated 5.0 by",
      title2: " 222 clients.",
      googleReviews: "Google Reviews",
      recommend: "100% Recommend",
    },
    instructor: {
      label: "Meet Your Instructors",
      title1: "Knowledgeable.",
      title2: "Personable. Serious about safety.",
      body: "Every class is led by a GSSC Certified Instructor — professional, patient and genuinely passionate about powered industrial truck safety, breaking everything down clearly until every operator feels confident behind the controls.",
      role: "OSHA-Authorized Trainers",
      name: "GSSC Certified Instructors",
      tags: ["Forklift", "Aerial", "Train-the-Trainer"],
      creds: [
        "Certified OSHA-authorized instructor",
        "Bilingual training — English & Spanish",
        "Focused on zero-accident workplaces",
        "Thousands of operators certified",
      ],
      stats: ["Google Rating", "Verified Reviews", "Hour Onsite Class", "Success Rate"],
      cta: "Book GSSC For Your Team",
    },
    gallery: {
      label: "In The Field",
      title1: "Real training.",
      title2: "Real jobsites.",
      intro:
        "A look at Golden State Safety operators getting certified onsite across California and beyond.",
      items: [
        { caption: "A Whole Crew Certified", tag: "Group Training" },
        { caption: "Certified & Card In Hand", tag: "Graduates" },
        { caption: "Train-the-Trainer Graduate", tag: "Certified" },
        { caption: "Certified On-Site", tag: "Graduates" },
        { caption: "On-The-Job Forklift Fleet", tag: "Forklift" },
        { caption: "Electric Forklift Fleet", tag: "Class I" },
        { caption: "Narrow-Aisle Reach Truck", tag: "Class II" },
        { caption: "Warehouse Ready", tag: "Onsite" },
        { caption: "Aerial Boom Lift Certified", tag: "Aerial" },
        { caption: "Rough-Terrain Telehandler", tag: "Class VII" },
      ],
    },
    quote: {
      label: "Schedule Training",
      title1: "Get your",
      title2: "instant quote.",
      intro:
        "Tell us about your team and equipment. We'll come to your facility in English or Spanish and get your operators certified — usually in about 2.5 hours.",
      name: "Full name *",
      company: "Company",
      email: "Email *",
      phone: "Phone *",
      trainees: "Number of trainees",
      language: "Preferred language",
      courses: "Courses needed",
      referralLabel: "How were you referred? *",
      referralPlaceholder: "Select an option",
      referralPersonLabel: "Name of who referred you *",
      toastReferral: "Please tell us how you were referred.",
      toastReferralName: "Please enter the name of the person who referred you.",
      location: "Facility location",
      street: "Street address",
      city: "City",
      state: "State",
      zip: "ZIP code",
      date: "Preferred date",
      more: "Anything else?",
      morePh: "Equipment types, timeline, questions…",
      submit: "Send my quote request",
      sending: "Sending…",
      langOptions: ["English", "Spanish", "Bilingual"],
      courseOptions: ["Forklift Certification", "Aerial / Scissor Lift", "Combo (Forklift + Aerial)", "Train-the-Trainer"],
      successTitle: "Request received",
      successBody: "Thanks! A GSSC Certified Instructor will contact you shortly to lock in your training date. Need it sooner? Call",
      another: "Submit another request",
      toastOk: "Quote request sent! A GSSC Certified Instructor will reach out shortly.",
      toastMissing: "Please fill in your name, email and phone.",
      toastErr: "Something went wrong. Please call us at",
    },
    footer: {
      desc: "California's #1 instructional safety certifications. OSHA-compliant forklift and aerial lift operator training — delivered onsite in English or Spanish.",
      serving: "Serving all of California & beyond",
      explore: "Explore",
      exploreLinks: ["Programs", "Classifications", "Pricing", "Reviews", "Get a Quote"],
      whyTitle: "Why It Matters",
      benefits: [
        "Required by OSHA regulations",
        "Avoid costly OSHA fines & penalties",
        "Boost productivity and profit",
        "Increase operator safety",
        "Reduce product & equipment damage",
        "Class instruction in ~2.5 hours",
      ],
      rights: "All rights reserved.",
      compliant: "Cal/OSHA Compliant",
    },
    sticky: { email: "Email us" },
  },

  es: {
    nav: {
      programs: "Programas",
      classifications: "Clasificaciones",
      pricing: "Precios",
      reviews: "Reseñas",
      gallery: "Galería",
      instructor: "Instructores",
      getQuote: "Cotizar",
      getInstantQuote: "Cotización Instantánea",
    },
    hero: {
      reviews: "222 Reseñas de Google",
      tagline: "Certificaciones de Seguridad #1 de California",
      lines: ["CERTIFICADO", "PARA OPERAR.", "HECHO PARA"],
      last: "DURAR.",
      sub: "Certificación de operadores de montacargas y elevadores aéreos conforme a OSHA — en su instalación en aproximadamente 2.5 horas. Impartido por un Instructor Certificado GSSC.",
      cta: "Cotización Instantánea",
      call: "Llame",
      badges: ["Inglés y Español", "Las 7 Clases de Montacargas", "Tarjetas con Foto"],
    },
    marquee: [
      "Conforme a Cal/OSHA",
      "222 Reseñas de 5 Estrellas",
      "Capacitación en su Instalación",
      "Tarjetas de Autorización con Foto",
      "Instrucción en Inglés y Español",
      "Instructores Certificados GSSC",
      "Las 7 Clasificaciones de Montacargas",
    ],
    manifesto: {
      label: "Por Qué Certificarse con GSSC",
      title1: "La seguridad es una",
      title2: "ventaja competitiva.",
      intro:
        "Cuatro razones por las que los empleadores de California confían en Golden State Safety Certifications para mantener a sus operadores en cumplimiento, seguros y productivos.",
      items: [
        { no: "01", title: "Cero Multas de OSHA", body: "La certificación del operador no es opcional — es ley federal. Mantenemos a su equipo en cumplimiento y a su empresa libre de multas que pueden alcanzar seis cifras por infracción." },
        { no: "02", title: "Dominio Onsite en 2.5 Horas", body: "Vamos a su instalación y certificamos a su equipo en aproximadamente dos horas y media. Sin viajes, sin tiempo muerto, sin pérdida de producción — capacitación a su horario." },
        { no: "03", title: "Las 7 Clasificaciones", body: "Desde montacargas eléctricos hasta montacargas todoterreno y elevadores aéreos, cubrimos cada clase de OSHA. Un instructor, todo el equipo que usted opera." },
        { no: "04", title: "Capacitación de Capacitadores", body: "Cree su propio programa interno en cumplimiento. Nuestro kit de certificación capacita a su instructor designado para certificar hasta 20 operadores — en inglés y español." },
      ],
    },
    services: {
      label: "Programas de Certificación",
      title1: "Cada operador.",
      title2: "Cada máquina.",
      intro:
        "Programas personalizados realizados en su sitio — en inglés o español — para cumplir todos los requisitos de Cal/OSHA para la industria general y de construcción.",
      requestCta: "Solicitar este programa",
      includesTitle: "Todo Programa de Seguridad Incluye",
      includes: [
        "Habilidades de manejo seguro",
        "Procedimientos de manejo adecuados",
        "Capacitación combinada — montacargas y aéreo",
        "Recarga de combustible y baterías",
        "Inspección diaria del equipo",
        "Equipo de pasillo angosto",
      ],
      items: [
        { tag: "Programa 01", title: "Certificación de Operador de Montacargas", desc: "Certificación conforme a OSHA en cada tipo de camión industrial motorizado — en su sitio en inglés o español.", points: ["Almacén y Contrapeso", "Reach Truck y Order Picker", "Telehandler y Todoterreno", "Patín y Apilador Eléctrico"] },
        { tag: "Programa 02", title: "Certificación de Elevadores Aéreos y de Tijera", desc: "Programas de seguridad para elevadores aéreos convenientes y económicos que cumplen los más altos estándares de Cal/OSHA.", points: ["Operación de Elevador de Tijera", "Elevadores de Pluma y Articulados", "Protocolos de Protección contra Caídas", "Evaluación de Riesgos en el Sitio"] },
        { tag: "Programa 03", title: "Certificación de Capacitador de Capacitadores", desc: "Desarrolle un programa interno de capacitación conforme a la normativa, dirigido por su propio capacitador certificado.", points: ["Kit del Programa — 20 Certificaciones", "Materiales en Español", "Programa de Certificación $400", "Kit de Instrucción $600"] },
      ],
    },
    classes: {
      label: "Clases de Montacargas OSHA",
      title1: "Certificamos las",
      title2: " 7 clasificaciones.",
      note: "La certificación incluye clase teórica, evaluación práctica y tarjeta con foto.",
      items: [
        { cls: "Clase I", name: "Montacargas Eléctricos con Operador", use: "Montacargas de contrapeso, sentados y de pie, usados en muelles de carga y almacenaje general." },
        { cls: "Clase II", name: "Montacargas Eléctricos de Pasillo Angosto", use: "Reach trucks y order pickers diseñados para almacenaje de alta densidad en pasillos angostos." },
        { cls: "Clase III", name: "Montacargas Eléctricos Manuales", use: "Apiladores y patines eléctricos para movimiento de material a nivel de piso." },
        { cls: "Clase IV", name: "Combustión Interna — Llanta Maciza", use: "Montacargas de llanta maciza para superficies interiores lisas y cargas pesadas." },
        { cls: "Clase V", name: "Combustión Interna — Llanta Neumática", use: "Montacargas robustos de llanta neumática para patios exteriores y terreno mixto." },
        { cls: "Clase VI", name: "Tractores Eléctricos y de Combustión", use: "Tractores de remolque usados para jalar en lugar de levantar en instalaciones grandes." },
        { cls: "Clase VII", name: "Montacargas Todoterreno", use: "Telehandlers de servicio pesado para obras de construcción y terreno irregular." },
      ],
    },
    pricing: {
      label: "Precios Transparentes",
      title1: "Mientras más capacita,",
      title2: "menos paga.",
      intro:
        "Precios de certificación por persona. Los códigos de clase adicionales cuestan solo $65 cada uno. Descuentos por volumen aplicados automáticamente.",
      popular: "Popular",
      perPerson: "/persona",
      addCode: "+ $65 por código de clase adicional",
      tierNotes: ["Equipos pequeños e individuos", "Equipos en crecimiento", "Mejor valor por operador"],
      tierNames: ["1–5 Alumnos", "6–10 Alumnos", "11+ Alumnos"],
      calcTitle: "Calcule su costo",
      operators: "Número de operadores",
      codes: "Códigos de clase de equipo",
      rate: "Tarifa",
      perPersonShort: "/persona",
      estTotal: "Total estimado",
      lockIn: "Asegurar esta cotización",
      riskLabel: "El costo del incumplimiento",
      riskBody:
        "Multa federal máxima de OSHA por infracción intencional o repetida por operadores sin certificar. La certificación se paga sola muchas veces.",
      yourCost: "su costo de cumplimiento",
      trainer1Title: "Certificación de Capacitador de Capacitadores",
      trainer1Body: "Certifique a su capacitador interno designado. Material de instrucción en español disponible.",
      trainer2Title: "Kit de Instrucción para Capacitadores",
      trainer2Body: "Kit completo con materiales para 20 certificaciones — cree su propio programa en cumplimiento.",
    },
    reviews: {
      label: "Reseñas Verificadas",
      title1: "Calificado 5.0 por",
      title2: " 222 clientes.",
      googleReviews: "Reseñas de Google",
      recommend: "100% lo Recomiendan",
    },
    instructor: {
      label: "Conozca a sus Instructores",
      title1: "Conocedor.",
      title2: "Cercano. Serio sobre la seguridad.",
      body: "Cada clase es impartida por un Instructor Certificado GSSC — profesional, paciente y genuinamente apasionado por la seguridad de los camiones industriales, explicando todo con claridad hasta que cada operador se siente seguro al mando.",
      role: "Instructores Autorizados por OSHA",
      name: "Instructores Certificados GSSC",
      tags: ["Montacargas", "Aéreo", "Capacitador"],
      creds: [
        "Instructor autorizado y certificado por OSHA",
        "Capacitación bilingüe — inglés y español",
        "Enfocado en lugares de trabajo sin accidentes",
        "Miles de operadores certificados",
      ],
      stats: ["Calificación Google", "Reseñas Verificadas", "Horas de Clase Onsite", "Tasa de Éxito"],
      cta: "Reserve a GSSC para su equipo",
    },
    gallery: {
      label: "En Acción",
      title1: "Capacitación real.",
      title2: "Sitios reales.",
      intro:
        "Un vistazo a los operadores de Golden State Safety certificándose en sitio por todo California y más allá.",
      items: [
        { caption: "Toda una Cuadrilla Certificada", tag: "Capacitación Grupal" },
        { caption: "Certificados y con Tarjeta en Mano", tag: "Graduados" },
        { caption: "Graduado de Capacitador de Capacitadores", tag: "Certificado" },
        { caption: "Certificados en el Sitio", tag: "Graduados" },
        { caption: "Flota de Montacargas en el Trabajo", tag: "Montacargas" },
        { caption: "Flota de Montacargas Eléctricos", tag: "Clase I" },
        { caption: "Reach Truck de Pasillo Angosto", tag: "Clase II" },
        { caption: "Almacén Listo", tag: "Onsite" },
        { caption: "Elevador de Pluma Certificado", tag: "Aéreo" },
        { caption: "Telehandler Todoterreno", tag: "Clase VII" },
      ],
    },
    quote: {
      label: "Agendar Capacitación",
      title1: "Obtenga su",
      title2: "cotización instantánea.",
      intro:
        "Cuéntenos sobre su equipo y maquinaria. Vamos a su instalación en inglés o español y certificamos a sus operadores — normalmente en unas 2.5 horas.",
      name: "Nombre completo *",
      company: "Empresa",
      email: "Correo *",
      phone: "Teléfono *",
      trainees: "Número de alumnos",
      language: "Idioma preferido",
      courses: "Cursos requeridos",
      referralLabel: "¿Cómo se enteró de nosotros? *",
      referralPlaceholder: "Seleccione una opción",
      referralPersonLabel: "Nombre de quien lo refirió *",
      toastReferral: "Por favor indíquenos cómo se enteró de nosotros.",
      toastReferralName: "Por favor ingrese el nombre de quien lo refirió.",
      location: "Ubicación de la instalación",
      street: "Dirección",
      city: "Ciudad",
      state: "Estado",
      zip: "Código postal",
      date: "Fecha preferida",
      more: "¿Algo más?",
      morePh: "Tipos de equipo, fechas, preguntas…",
      submit: "Enviar mi solicitud",
      sending: "Enviando…",
      langOptions: ["Inglés", "Español", "Bilingüe"],
      courseOptions: ["Certificación de Montacargas", "Elevador Aéreo / de Tijera", "Combo (Montacargas + Aéreo)", "Capacitador de Capacitadores"],
      successTitle: "Solicitud recibida",
      successBody: "¡Gracias! Un Instructor Certificado GSSC lo contactará pronto para confirmar su fecha de capacitación. ¿Lo necesita antes? Llame al",
      another: "Enviar otra solicitud",
      toastOk: "¡Solicitud enviada! Un Instructor Certificado GSSC se comunicará pronto.",
      toastMissing: "Por favor complete su nombre, correo y teléfono.",
      toastErr: "Algo salió mal. Por favor llámenos al",
    },
    footer: {
      desc: "Las certificaciones de seguridad #1 de California. Capacitación de operadores de montacargas y elevadores aéreos conforme a OSHA — en su sitio en inglés o español.",
      serving: "Sirviendo todo California y más allá",
      explore: "Explorar",
      exploreLinks: ["Programas", "Clasificaciones", "Precios", "Reseñas", "Cotizar"],
      whyTitle: "Por Qué Importa",
      benefits: [
        "Requerido por las regulaciones de OSHA",
        "Evite costosas multas de OSHA",
        "Aumente la productividad y ganancia",
        "Aumente la seguridad del operador",
        "Reduzca el daño a producto y equipo",
        "Instrucción en clase de ~2.5 horas",
      ],
      rights: "Todos los derechos reservados.",
      compliant: "Conforme a Cal/OSHA",
    },
    sticky: { email: "Escríbanos" },
  },
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("gssc_lang") || "en");
  useEffect(() => {
    localStorage.setItem("gssc_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  const toggle = () => setLang((l) => (l === "en" ? "es" : "en"));
  return (
    <LanguageContext.Provider value={{ lang, setLang, toggle, t: content[lang] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
